import { SubscriptionUpdateType } from "@coeff/api";
import { message, Skeleton } from "antd";
import React, { useCallback, useContext, useState } from "react";
import styled from "styled-components";

import { useBillingDetails } from "../../../api/users";
import { Button, ConfirmBox } from "../../../components";
import { COLORS } from "../../../constants";
import { BContext } from "../../../types";
import { numberWithCommas, useTrack } from "../../../utils";
import { BillingContext } from "../Billing";
import { SELF_SERVE_WEB_DASHBOARD_LIMIT } from "../webDashboardEligibility.utils";

// Self-serve AI Dashboards limit copy, shared by the pricing-page tooltip and this modal.
export const WEB_DASHBOARD_BILLED_PER_BUILDER = "Billed per builder.";
export const WEB_DASHBOARD_MAX_PER_BUILDER = `Max ${SELF_SERVE_WEB_DASHBOARD_LIMIT} active dashboards per builder`;

type Props = {
  visible: boolean;
  onClose: () => void;
};

type UpdateSubResponse = { is_success?: boolean; error_msg?: string };

const FOOTER_BUTTON_MIN_WIDTH = "132px";

/** "Confirm plan changes" dialog, matching the seat-change confirmation in ManageTeam. */
const ConfirmModal = styled(ConfirmBox)`
  && .ant-modal-content .ant-modal-body {
    font-size: 14px;
    padding-bottom: 12px;
  }
`;

const BackButton = styled(Button)`
  &&.ant-btn-default {
    min-width: ${FOOTER_BUTTON_MIN_WIDTH};
    background-color: ${COLORS.coeblue11};
    border-color: ${COLORS.coeblue11};

    &:active,
    &:hover,
    &:focus {
      background-color: ${COLORS.coeblue12};
      border-color: ${COLORS.coeblue12};
    }
  }
`;

const SummaryBox = styled.div`
  background: ${COLORS.coeblue11};
  border-radius: 8px;
  padding: 8px 16px 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 8px 0px;
  color: ${COLORS.black85};

  &.discount {
    color: ${COLORS.discountGreen};
  }

  &.large {
    font-size: 20px;
    font-weight: bold;
  }
`;

const DueNowBox = styled.div`
  background: white;
  border-radius: 8px;
  padding: 8px 16px;
  margin-top: 12px;

  .note {
    font-size: 12px;
    color: ${COLORS.black45};
    margin-bottom: 8px;
  }
`;

/** Purchase flow for the AI Dashboards addon on an existing eligible Pro subscription. */
export const AddWebDashboardModal: React.FC<Props> = ({ visible, onClose }) => {
  const { billingMetadata, userSubscription, updateSubscription, syncBilling, eventFrom } =
    useContext(BillingContext) as BContext;

  const track = useTrack();

  const [submitting, setSubmitting] = useState(false);

  const subscriptionDetails = userSubscription?.subscription_details;

  const billingInterval = subscriptionDetails?.billing_interval ?? "annually";

  const addonPriceFor = (interval: string) =>
    billingMetadata?.plans.find(p => p.plan_type === "pro" && p.billing_interval === interval)
      ?.web_dashboard_addon?.per_item_monthly_price_usd ?? 0;

  const addonMonthlyPrice = addonPriceFor(billingInterval);
  // List price is the month-to-month rate; on annual billing the gap is the annual savings.
  const addonListPrice = Math.max(addonPriceFor("monthly"), addonMonthlyPrice);
  const quantity = subscriptionDetails?.max_users ?? 1;
  const cycleMonths = billingInterval === "annually" ? 12 : 1;

  // Server-side Chargebee estimate for adding the addon to the CURRENT subscription shape
  // (same plan/seats/sources + the addon): amount_due is the prorated charge for the
  // remainder of the billing cycle.
  const { data: estimateData, isLoading: estimateLoading } = useBillingDetails(
    {
      temporary_front_end_estimated_amount: addonMonthlyPrice,
      plan_type: subscriptionDetails?.plan_type,
      billing_interval: subscriptionDetails?.billing_interval,
      num_users: subscriptionDetails?.max_users ?? 1,
      num_data_sources: subscriptionDetails?.max_data_source_types ?? 1,
      subscription_id: subscriptionDetails?.subscription_id,
      include_web_dashboards: true,
    },
    { enabled: visible && Boolean(subscriptionDetails?.subscription_id) }
  );
  const billingDetails = estimateData?.billing_details;

  // Line items, each derived so they sum to the server's amount due:
  // list total - annual savings - proration - credits - coupon = amount_due.
  const listTotal = addonListPrice * quantity * cycleMonths;
  const annualSavings = (addonListPrice - addonMonthlyPrice) * quantity * cycleMonths;
  const fullTermTotal = listTotal - annualSavings;
  const proratedDiscount = billingDetails
    ? Math.max(0, fullTermTotal - billingDetails.amount_total)
    : 0;
  // Kept when credits cover the whole charge (amount_due 0): the server then reports the covered
  // total as existing_credits, and dropping it would leave the rows not summing to $0. Negative
  // values (refund estimates) are filtered out by the > 0 render check.
  const existingCredits = billingDetails?.existing_credits ?? 0;
  const couponDiscount = billingDetails?.discount_price ?? 0;

  const handleClose = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [onClose, submitting]);

  const handleConfirm = useCallback(async () => {
    if (!subscriptionDetails?.subscription_id) {
      message.error("Missing subscription. Please refresh and try again.");
      return;
    }

    track("billing_ai_dashboards_purchase_clicked", {
      current_plan_type: subscriptionDetails?.plan_type,
      event_from: eventFrom,
    });

    setSubmitting(true);
    try {
      const res = (await updateSubscription({
        update_type: SubscriptionUpdateType.AddWebDashboardAddon,
        subscription_id: subscriptionDetails.subscription_id,
      })) as UpdateSubResponse;

      if (res?.is_success) {
        track("billing_ai_dashboards_purchase_succeeded", {
          current_plan_type: subscriptionDetails?.plan_type,
          event_from: eventFrom,
        });
        await syncBilling();
        onClose();
        message.success("AI dashboards added to your plan");
      } else {
        track("billing_ai_dashboards_purchase_failed", {
          current_plan_type: subscriptionDetails?.plan_type,
          event_from: eventFrom,
          reason: res?.error_msg,
        });
        message.error(res?.error_msg ?? "Something went wrong.");
      }
    } catch (e: unknown) {
      track("billing_ai_dashboards_purchase_failed", {
        current_plan_type: subscriptionDetails?.plan_type,
        event_from: eventFrom,
        reason: e instanceof Error ? e.message : undefined,
      });
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setSubmitting(false);
    }
  }, [subscriptionDetails, updateSubscription, syncBilling, onClose, track, eventFrom]);

  if (!subscriptionDetails) {
    return null;
  }

  return (
    <ConfirmModal
      visible={visible}
      title="Confirm plan changes"
      width={560}
      closable={true}
      onCancel={handleClose}
      cancelButton={
        <BackButton type="default" disabled={submitting}>
          Back
        </BackButton>
      }
      okText="Confirm & Pay"
      onOk={handleConfirm}
      confirmLoading={submitting}
      confirmDisabled={estimateLoading}
      okButtonProps={{ style: { minWidth: FOOTER_BUTTON_MIN_WIDTH } }}
    >
      <p>
        AI Dashboards will be added to your plan. Billed per builder (max{" "}
        {SELF_SERVE_WEB_DASHBOARD_LIMIT} active dashboards per builder).
      </p>

      <SummaryBox>
        <SummaryRow>
          <span>AI Dashboards Add-on</span>
          <span>${numberWithCommas(listTotal)}</span>
        </SummaryRow>

        {estimateLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : (
          <DueNowBox>
            {annualSavings > 0 && (
              <SummaryRow className="discount">
                <span>Annual discount</span>
                <span>-${numberWithCommas(annualSavings)}</span>
              </SummaryRow>
            )}
            {proratedDiscount > 0 && (
              <SummaryRow className="discount">
                <span>Prorated discount for the current billing cycle</span>
                <span>-${numberWithCommas(proratedDiscount)}</span>
              </SummaryRow>
            )}
            {existingCredits > 0 && (
              <SummaryRow className="discount">
                <span>Existing credits applied</span>
                <span>-${numberWithCommas(existingCredits)}</span>
              </SummaryRow>
            )}
            {couponDiscount > 0 && (
              <SummaryRow className="discount">
                <span>{billingDetails?.discount_coupon ?? "Discount"} applied</span>
                <span>-${numberWithCommas(couponDiscount)}</span>
              </SummaryRow>
            )}
            {billingDetails && (
              <SummaryRow className="large">
                <span>Amount due now</span>
                <span>${numberWithCommas(billingDetails.amount_due)}</span>
              </SummaryRow>
            )}
            <div className="note">
              Your card ending in {subscriptionDetails.cc_last_4_digits} will be charged a prorated
              amount for the remainder of your current billing cycle. AI Dashboards will be active
              immediately.
            </div>
          </DueNowBox>
        )}
      </SummaryBox>
    </ConfirmModal>
  );
};
